import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking");
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setStatus("error"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setStatus("paid"); refresh(); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setStatus("error"); return; }
      } catch (e) { console.error("Payment status poll failed:", e); }
      polls.current += 1;
      if (polls.current > 12) { setStatus("timeout"); return; }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--bg-secondary)]">
      <div className="bg-white rounded-2xl border border-[var(--border-subtle)] p-10 max-w-md w-full text-center" data-testid="payment-success-card">
        {status === "checking" && (<><Loader2 className="w-12 h-12 animate-spin text-[var(--gold)] mx-auto" /><h1 className="font-serif text-2xl mt-5">Confirming your payment…</h1><p className="text-[var(--text-muted)] text-sm mt-2">This will only take a moment.</p></>)}
        {status === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 text-[var(--escrow-green)] mx-auto" />
          <h1 className="font-serif text-3xl mt-5">Payment received</h1>
          <p className="text-[var(--text-secondary)] mt-3">Thank you. The artist has been notified to ship your artwork. You'll confirm delivery to complete the order and pay the artist.</p>
          <button onClick={() => navigate("/buyer")} className="btn-gold mt-6 px-7 py-3 rounded-full text-sm font-semibold w-full">Go to my orders</button>
        </>)}
        {(status === "error" || status === "timeout") && (<>
          <XCircle className="w-14 h-14 text-[var(--destructive)] mx-auto" />
          <h1 className="font-serif text-2xl mt-5">{status === "timeout" ? "Still processing" : "Something went wrong"}</h1>
          <p className="text-[var(--text-muted)] text-sm mt-2">{status === "timeout" ? "Your payment is taking longer than usual. Check your orders shortly." : "We couldn't confirm this payment."}</p>
          <Link to="/buyer" className="btn-gold inline-block mt-6 px-7 py-3 rounded-full text-sm font-semibold">View my orders</Link>
        </>)}
      </div>
    </div>
  );
}
